import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Button,
  Chip,
  Collapse,
  Divider,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ReplayIcon from "@mui/icons-material/Replay";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";

import { transformOrderId } from "util/orderUtils";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { loadOrdersSummary, addDishToCart } from "store/actions/order";
import { decodeRestaurantId } from "util/utils";

const OrderDetail = (props) => {
  const ordersSummary = useSelector((state) => state.order.ordersSummary);
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const restaurant = useSelector((state) => state.menu.restaurant);

  const id = props.match.params.menuId;
  const tableId = props.match.params.tableId;
  const loadOrder = (restaurant) => dispatch(loadOrdersSummary(restaurant));

  const [expandedOrders, setExpandedOrders] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const currencySymbol =
    restaurant && restaurant.currencySymbol
      ? restaurant.currencySymbol
      : "$";

  const itemTotal = (orderSummary) => {
    if (orderSummary && orderSummary.items) {
      return orderSummary.items
        .filter(
          (item) =>
            item.permanentDeletion !== true && item.isVoidedDish !== true
        )
        .reduce((count, curItem) => {
          return count + curItem.quantity * curItem.subTotal;
        }, 0);
    }
    return 0;
  };

  const surchargeTotal = (orderSummary) => {
    if (orderSummary && orderSummary.surcharges) {
      return orderSummary.surcharges.reduce((sum, s) => {
        return sum + (parseFloat(s.amount) || 0);
      }, 0);
    }
    return 0;
  };

  const orderGrandTotal = (orderSummary) => {
    return itemTotal(orderSummary) + surchargeTotal(orderSummary);
  };

  const goBack = () => {
    history.push("/online/" + id + "/" + tableId);
  };

  const toggleExpand = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleReorder = (orderSummary) => {
    if (!orderSummary || !orderSummary.items) return;

    const validItems = orderSummary.items.filter(
      (item) =>
        item.permanentDeletion !== true &&
        item.isVoidedDish !== true &&
        item.dish
    );

    validItems.forEach((item) => {
      const cartItem = {
        dish: item.dish,
        quantity: item.quantity,
        subTotal: item.subTotal,
        options: item.options || [],
        size: item.size || null,
        specialInstructions: item.specialInstructions || "",
      };
      dispatch(addDishToCart(cartItem));
    });

    setSnackbar({
      open: true,
      message: `${validItems.length} item${validItems.length > 1 ? "s" : ""} added to cart`,
    });

    setTimeout(() => {
      history.push(`/cart/${id}/${tableId}`);
    }, 800);
  };

  const getPaymentChip = (orderSummary) => {
    if (orderSummary.paymentType && orderSummary.paymentType !== "") {
      return (
        <Chip
          label="Paid"
          size="small"
          sx={{
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      );
    }
    return (
      <Chip
        label="Unpaid"
        size="small"
        sx={{
          backgroundColor: "#fff3e0",
          color: "#e65100",
          fontWeight: 600,
          fontSize: "0.75rem",
        }}
      />
    );
  };

  useEffect(() => {
    if (id) {
      loadOrder(decodeRestaurantId(id));
    }
  }, [id]);

  useEffect(() => {
    if (ordersSummary && ordersSummary.length > 0) {
      setExpandedOrders({ [ordersSummary[0].id]: true });
    }
  }, [ordersSummary]);

  const validOrders =
    ordersSummary?.filter(
      (o) => o && o.items && o.items.length > 0
    ) || [];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: "primary.main",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            onClick={goBack}
            edge="start"
            color="inherit"
            aria-label="back"
            sx={{ mr: 1 }}
          >
            <ArrowBackIosIcon fontSize="small" />
          </IconButton>
          <ReceiptLongIcon sx={{ mr: 1, fontSize: 24 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1rem", sm: "1.15rem" },
              flex: 1,
            }}
          >
            My Orders
          </Typography>
          {validOrders.length > 0 && (
            <Chip
              label={`${validOrders.length} order${validOrders.length > 1 ? "s" : ""}`}
              size="small"
              sx={{
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "#fff",
                fontWeight: 600,
              }}
            />
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 600, mx: "auto" }}>
        {validOrders.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 3,
              mt: 4,
            }}
          >
            <ReceiptLongIcon
              sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              No Orders Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your order history will appear here once you place an order.
            </Typography>
            <Button
              variant="contained"
              onClick={goBack}
              sx={{ textTransform: "none", borderRadius: 2, px: 4 }}
            >
              Browse Menu
            </Button>
          </Paper>
        ) : (
          validOrders.map((orderSummary, index) => {
            const isExpanded = expandedOrders[orderSummary.id] ?? false;
            const items = orderSummary.items.filter(
              (item) =>
                item.permanentDeletion !== true &&
                item.isVoidedDish !== true
            );
            const totalItems = items.reduce(
              (sum, item) => sum + item.quantity,
              0
            );

            return (
              <Paper
                key={orderSummary.id}
                elevation={0}
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: index === 0 ? "primary.light" : "divider",
                  transition: "box-shadow 0.2s",
                  "&:hover": {
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                  },
                }}
              >
                {/* Order header */}
                <Box
                  onClick={() => toggleExpand(orderSummary.id)}
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor:
                        index === 0 ? "primary.main" : "grey.200",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        color: index === 0 ? "#fff" : "text.secondary",
                      }}
                    >
                      #{transformOrderId(orderSummary.id)}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.25,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.95rem",
                        }}
                      >
                        Order #{transformOrderId(orderSummary.id)}
                      </Typography>
                      {getPaymentChip(orderSummary)}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <AccessTimeIcon
                        sx={{ fontSize: 14, color: "text.secondary" }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        {orderSummary.orderTime}
                      </Typography>
                      {orderSummary.realTableId && (
                        <>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mx: 0.5 }}
                          >
                            ·
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.8rem" }}
                          >
                            Table {orderSummary.realTableId}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 0.25,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: "primary.main",
                      }}
                    >
                      {currencySymbol}
                      {orderGrandTotal(orderSummary).toFixed(2)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {totalItems} item{totalItems > 1 ? "s" : ""}
                    </Typography>
                  </Box>

                  <IconButton size="small" sx={{ ml: 0.5 }}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Expanded order details */}
                <Collapse in={isExpanded}>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    {items.map((row, itemIdx) => (
                      <Box
                        key={row.id || itemIdx}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          py: 1.25,
                          borderBottom:
                            itemIdx < items.length - 1
                              ? "1px solid"
                              : "none",
                          borderColor: "divider",
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            backgroundColor: "grey.100",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            mr: 1.5,
                            mt: 0.25,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              color: "text.secondary",
                            }}
                          >
                            {row.quantity}x
                          </Typography>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.9rem",
                              lineHeight: 1.3,
                            }}
                          >
                            {row.dish.name}
                            {row.size ? ` (${row.size.name})` : ""}
                          </Typography>
                          {row.dish.altName && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                fontSize: "0.8rem",
                              }}
                            >
                              {row.dish.altName}
                            </Typography>
                          )}
                          {row.options && row.options.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              {row.options.map((option) => (
                                <Typography
                                  key={option.id}
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    color: "text.secondary",
                                    fontSize: "0.75rem",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  + {option.name} x{option.quantity}{" "}
                                  {currencySymbol}
                                  {parseFloat(option.price).toFixed(2)}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>

                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.9rem",
                            color: "text.primary",
                            flexShrink: 0,
                            ml: 1,
                          }}
                        >
                          {currencySymbol}
                          {(row.quantity * row.subTotal).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}

                    {/* Subtotal */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        pt: 1.5,
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Subtotal
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {currencySymbol}
                        {itemTotal(orderSummary).toFixed(2)}
                      </Typography>
                    </Box>

                    {/* Surcharges */}
                    {orderSummary.surcharges &&
                      orderSummary.surcharges.map((surcharge) => (
                        <Box
                          key={surcharge.name}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            pt: 0.75,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            {surcharge.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500 }}
                          >
                            {currencySymbol}
                            {parseFloat(surcharge.amount).toFixed(2)}
                          </Typography>
                        </Box>
                      ))}

                    {/* Grand total */}
                    <Divider sx={{ my: 1.5 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography sx={{ fontWeight: 700 }}>Total</Typography>
                      <Typography
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      >
                        {currencySymbol}
                        {orderGrandTotal(orderSummary).toFixed(2)}
                      </Typography>
                    </Box>

                    {/* Special terms */}
                    {restaurant?.onlineSettings?.specialTerms && (
                      <Alert
                        severity="warning"
                        sx={{
                          mt: 1.5,
                          borderRadius: 2,
                          "& .MuiAlert-message": {
                            fontSize: "0.8rem",
                          },
                        }}
                      >
                        {restaurant.onlineSettings.specialTerms}
                      </Alert>
                    )}

                    {/* Reorder button 
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<ReplayIcon />}
                      onClick={() => handleReorder(orderSummary)}
                      sx={{
                        mt: 2,
                        py: 1.25,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        boxShadow: "none",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                      }}
                    >
                      Reorder
                    </Button>*/}
                  </Box>
                </Collapse>

                {/* Collapsed quick reorder */}
                {!isExpanded && (
                  <>
                    <Divider />
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: "grey.50",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: "0.8rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          mr: 1,
                        }}
                      >
                        {items
                          .slice(0, 3)
                          .map((item) => item.dish.name)
                          .join(", ")}
                        {items.length > 3 ? "..." : ""}
                      </Typography>
                    
                    </Box>
                  </>
                )}
              </Paper>
            );
          })
        )}

        {validOrders.length > 0 && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Button
              variant="outlined"
              onClick={goBack}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 4,
                fontWeight: 600,
              }}
            >
              Continue Ordering
            </Button>
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbar({ open: false, message: "" })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderDetail;
